import DividaDto from '@/models/dto/DividaDto';
import DividaResource from '@/models/resources/DividaResource';
import SincronizacaoResource from '@/models/resources/SincronizacaoResource';
import { getQueryClientInstance } from '@/plugins/vueQuery';
import { dividaService } from '@/services';
import ConvivenciaWlSevice from '@/services/ConvivenciaWlSevice';
import sessionService from '@/services/SessionService';
import storageService from '@/services/StorageService';
import { DividasQueryDto, ProcessedDividasDto } from './dto/useDividasQueriesDto';

/**
 * Obtém o hash do usuário atual para uso nas chaves de cache
 */
function getUserHash(): string | null {
  return sessionService.token?.payload.hash || storageService.hash.get() || null;
}

/**
 * Composable para gerenciar requisições de dívidas com Vue Query
 * Centraliza cache e invalidação automática
 * IMPORTANTE: Todas as chaves incluem o hash do usuário para evitar conflitos entre usuários
 */
export const dividasKeys = {
  all: ['dividas'] as const,
  lists: () => [...dividasKeys.all, 'list'] as const,
  list: (api: string) => {
    const userHash = getUserHash();
    return [...dividasKeys.lists(), api, userHash] as const;
  },
  details: () => [...dividasKeys.all, 'detail'] as const,
  detail: (api: string, id: number) => {
    const userHash = getUserHash();
    return [...dividasKeys.details(), api, id, userHash] as const;
  },
  syncAll: () => {
    const userHash = getUserHash() || 'anonymous';
    return [...dividasKeys.all, 'sync-all', userHash] as const;
  },
};

/**
 * Tipo de retorno da sincronização completa de dívidas
 * Extraído diretamente do retorno de sincronizarDividasViaAPI
 */
type SyncAllDividasReturn = {
  todasDividas: DividaResource[];
  sincronizouDividasPorApi: Record<string, boolean>;
  deveSincronizarPropostasPorApi: Record<string, boolean>;
  credoresSincronizados: string[];
};

const lancarSeAbortada = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    const err = new Error('Aborted');
    err.name = 'AbortError';
    throw err;
  }
};

function executarSeNaoAbortada<T>(
  operation: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  lancarSeAbortada(signal);
  const retorno = operation();
  lancarSeAbortada(signal);
  return retorno;
}

/**
 * Função centralizada para obter dados de dívidas
 * Encapsula toda a lógica de sincronização
 */
async function fetchDividasFromService(
  api: string,
  signal?: AbortSignal,
): Promise<DividasQueryDto> {
  const sincronizarResp = await executarSeNaoAbortada(
    () => ConvivenciaWlSevice.isWhiteLabel(api)
      ? dividaService.sincronizarWhitelabelApi(api, undefined, signal) 
      : dividaService.sincronizar(api, signal),
    signal,
  );

  return {
    dividas: sincronizarResp.dividas,
    infoCredor: sincronizarResp.infoCredor,
    source: ConvivenciaWlSevice.isWhiteLabel(api) ? 'whitelabel' : 'sincronizar',
    status: sincronizarResp.sincronizouDividas === false ? 'error' : 'success',
  };
}

/**
 * Processa dados brutos em recursos formatados
 */
function processDividasData(data: DividasQueryDto): ProcessedDividasDto {
  return {
    dividas: data.dividas.map((dividaData: unknown) => new DividaResource(dividaData as DividaDto)),
    infoCredor: data.infoCredor,
  };
}

/**
 * Função otimizada para obter dados do cache
 * Retorna informações completas sobre o cache
 */
function getCacheInfo(api: string) {
  const queryClient = getQueryClientInstance();
  const queryKey = dividasKeys.list(api);
  const cachedData: DividasQueryDto | undefined = queryClient.getQueryData(queryKey);

  return {
    exists: !!cachedData,
    hasError: !!(cachedData && cachedData.status === 'error'),
    data: cachedData?.status === 'success' ? cachedData : null,
  };
}

/**
 * Função para invalidar cache quando há mudança de usuário
 * Deve ser chamada no login para garantir que não há cache de outro usuário
 */
export function invalidateCacheOnUserChange(): void {
  const queryClient = getQueryClientInstance();
  // Remove todo o cache de dívidas (de qualquer usuário)
  queryClient.removeQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as string[];
      return queryKey[0] === 'dividas';
    },
  });
}

/**
 * Valida se o cache é do usuário atual
 */
function validateCacheUser(api: string): boolean {
  const queryClient = getQueryClientInstance();
  const currentUserHash = getUserHash();

  if (!currentUserHash) {
    return true; // Sem usuário logado, permite continuar
  }

  const queryKey = dividasKeys.list(api);
  const cachedData: DividasQueryDto | undefined = queryClient.getQueryData(queryKey);
  // Se há cache mas a chave não contém o hash do usuário atual, invalida
  if (cachedData && !queryKey.includes(currentUserHash)) {
    invalidateCacheOnUserChange();
    return false;
  }
  return true;
}

/**
 * Função principal para obter dados de dívidas usando Vue Query
 * Encapsula toda a lógica de cache e sincronização
 */
export async function fetchDividasData(api: string): Promise<ProcessedDividasDto> {
  const queryClient = getQueryClientInstance();
  // Valida se o cache é do usuário correto
  validateCacheUser(api);
  const queryKey = dividasKeys.list(api);

  const data = await queryClient.fetchQuery({
    queryKey,
    queryFn: ({ signal }) => fetchDividasFromService(api, signal),
    // Usa configurações globais do vueQuery.ts
  });

  if (data.status === 'error') {
    queryClient.removeQueries({ queryKey });
  }
  return processDividasData(data);
}

/**
 * Função para obter dados do cache de uma API específica
 */
export function getCachedDividasData(api: string): ProcessedDividasDto | null {
  // Valida se o cache é do usuário correto

  if (!validateCacheUser(api)) {
    return null;
  }

  const cacheInfo = getCacheInfo(api);

  if (!cacheInfo.data) {
    return null;
  }

  return processDividasData(cacheInfo.data);
}

/**
 * Função para verificar se existe cache de erro para uma API
 */
export function hasErrorCache(api: string): boolean {
  return getCacheInfo(api).hasError;
}

/**
 * Função para verificar se existe cache para uma API
 */
export function hasCache(api: string): boolean {
  return getCacheInfo(api).exists;
}

const STALE_MS = Number(appSettings.VUE_APP_VUE_QUERY_STALE_TIME) || 6 * 60 * 1000;

/** Cache de sync-all válido = endpoint de ordenação não deve ser chamado. */
export function temCacheSyncAllValido(): boolean {
  const qc = getQueryClientInstance();
  const key = dividasKeys.syncAll();
  const state = qc.getQueryState(key);
  const data = qc.getQueryData(key);
  if (!data || !state?.dataUpdatedAt) return false;
  return Date.now() - state.dataUpdatedAt <= STALE_MS;
}

/**
 * Emite chunks do cache simulando comportamento de streaming
 */
function emitChunksFromCache(
  cacheData: SyncAllDividasReturn,
  onChunk: (chunk: SincronizacaoResource, veioDoCache?: boolean, api?: string) => void,
): void {
  const dividasPorApi = new Map<string, DividaResource[]>();
  cacheData.todasDividas.forEach((divida: DividaResource) => {
    const { api } = divida;
    if (!dividasPorApi.has(api)) {
      dividasPorApi.set(api, []);
    }
    dividasPorApi.get(api)?.push(divida);
  });

  const sincronizouDividasPorApi = cacheData.sincronizouDividasPorApi || {};

  dividasPorApi.forEach((dividas, api) => {
    const sincronizouDividas = sincronizouDividasPorApi[api] ?? true;

    queueMicrotask(() => {
      if (!sessionService.token) return;
      onChunk(new SincronizacaoResource({
        dividas: dividas as unknown as DividaDto[],
        sincronizouDividas,
        periodoIndisponibilidade: undefined,
      }), true, api);
    });
  });
}

/**
 * Sincroniza todas as dívidas usando Vue Query para cache
 * Usa fetchQuery corretamente para gerenciar cache automaticamente
 */
export async function syncAllDividas(
  onChunk: (chunk: SincronizacaoResource, veioDoCache?: boolean, api?: string) => void,
): Promise<SyncAllDividasReturn> {
  const queryClient = getQueryClientInstance();
  const queryKey = dividasKeys.syncAll();

  // Usa fetchQuery - Vue Query gerencia cache automaticamente
  const resultado = await queryClient.fetchQuery({
    queryKey,
    queryFn: async ({ signal }): Promise<SyncAllDividasReturn> => {
      const dadosCompletos = await dividaService.sincronizarDividasViaAPI(onChunk, signal);
      return dadosCompletos;
    },
    // Usa configurações globais do vueQuery.ts (staleTime, etc)
  });

  // Se veio do cache, emite os chunks
  const queryState = queryClient.getQueryState(queryKey);
  const isFromCache = queryState
    && queryState.dataUpdatedAt
    && Date.now() - queryState.dataUpdatedAt > 100;

  if (isFromCache) {
    // Se os dados foram atualizados há mais de 100ms, provavelmente veio do cache
    emitChunksFromCache(resultado, onChunk);
  }

  return resultado;
}
